import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchHelpGuide, fetchHelpGuides, fetchHelpSearchIndex, type IHelpGuideSummary, type IHelpSearchEntry } from '@apis/help';
import { useLang } from '@hooks/useLang';
import { helpGuideKey, helpGuidesKey, helpSearchIndexKey } from '@constants/queryKeys';

const GUIDES_STALE_MS = 10 * 60_000;
const NO_GUIDES: IHelpGuideSummary[] = [];
const NO_ENTRIES: IHelpSearchEntry[] = [];

/** `isPlaceholderData`: the list on hand is the previous language's, kept up until the new one arrives. */
export function useHelpGuides() {
  const language = useLang();
  const { data, isLoading, isError, isPlaceholderData } = useQuery({ queryKey: helpGuidesKey(language), queryFn: ({ signal }) => fetchHelpGuides(language, signal), staleTime: GUIDES_STALE_MS, placeholderData: keepPreviousData });
  return { guides: data ?? NO_GUIDES, isLoading, isError, isPlaceholderData };
}

/** Moving to another guide, or another language, keeps the current one on screen until the next arrives
 *  (`isSwitching`), rather than blanking the page in between. */
export function useHelpGuide(slug: string) {
  const language = useLang();
  const { data, isError, isPlaceholderData } = useQuery({
    queryKey: helpGuideKey(slug, language), queryFn: ({ signal }) => fetchHelpGuide(slug, language, signal), staleTime: GUIDES_STALE_MS, retry: false,
    placeholderData: keepPreviousData,
  });
  return { guide: data, isError, isSwitching: isPlaceholderData };
}

/** Every visible guide's text, fetched once for the hub to search in the browser. */
export function useHelpSearchIndex() {
  const language = useLang();
  const { data, isPending, isError } = useQuery({ queryKey: helpSearchIndexKey(language), queryFn: ({ signal }) => fetchHelpSearchIndex(language, signal), staleTime: GUIDES_STALE_MS, placeholderData: keepPreviousData });
  return { index: data ?? NO_ENTRIES, isPending, isError };
}
